import { useEffect, useRef, type RefObject } from "react";
import { cn } from "@/lib/utils";

interface LiveWaveformProps {
  /** 0–1 arası peak geçmişi; sağdaki en yeni örnek (ref — 60fps React re-render yok) */
  peaksRef: RefObject<number[]>;
  className?: string;
  barWidth?: number;
  barGap?: number;
}

/**
 * ChatGPT tarzı canlı dikte waveform'u.
 * Peak'ler sağdan sola akar; sağ kenarda soft fade/shadow maskesi var.
 */
export const LiveWaveform = ({
  peaksRef,
  className,
  barWidth = 3.5,
  barGap = 3,
}: LiveWaveformProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let rafId = 0;
    let disposed = false;

    const draw = () => {
      if (disposed) return;

      const dpr = window.devicePixelRatio || 1;
      const cssWidth = canvas.clientWidth;
      const cssHeight = canvas.clientHeight;
      if (cssWidth === 0 || cssHeight === 0) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      const pixelWidth = Math.round(cssWidth * dpr);
      const pixelHeight = Math.round(cssHeight * dpr);
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const isDark = document.documentElement.classList.contains("dark");
      const barRgb = isDark ? "229, 231, 235" : "75, 85, 99";
      const dotColor = isDark
        ? "rgba(156, 163, 175, 0.55)"
        : "rgba(107, 114, 128, 0.45)";

      const step = barWidth + barGap;
      const maxBars = Math.max(1, Math.floor(cssWidth / step));
      const centerY = cssHeight / 2;
      const maxBarHalf = cssHeight * 0.46;
      // Sessizlikte küçük pill barlar
      const minBarHalf = maxBarHalf * 0.18;
      const data = peaksRef.current ?? [];

      // Henüz örnek yoksa (kayıt henüz dolmadı) solda noktalı taban
      const filledBars = Math.min(data.length, maxBars);
      const emptyBars = Math.max(0, maxBars - filledBars);
      for (let i = 0; i < emptyBars; i++) {
        const x = barWidth / 2 + i * step;
        ctx.beginPath();
        ctx.fillStyle = dotColor;
        ctx.arc(x, centerY, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Fade penceresi: sağdan dar/keskin (ChatGPT trick)
      const fadeZone = cssWidth * 0.12;

      // Sağdan sola: en yeni peak en sağda — her örnek için min yükseklik garanti
      for (let i = 0; i < filledBars; i++) {
        const peakIndex = data.length - 1 - i;
        const raw = peakIndex >= 0 ? data[peakIndex] : 0;

        // Orta eğri: normal konuşma ~%75, peak'ler tavana yaklaşır ama hemen clip olmaz
        const amplified = Math.min(1, Math.tanh(Math.max(0, raw) * 2.15));
        const halfHeight = Math.max(minBarHalf, amplified * maxBarHalf);

        const x = cssWidth - (i + 1) * step + barGap / 2;
        if (x < -barWidth) continue;

        // Sağ kenara yakınlık → opacity düşür
        const distFromRight = cssWidth - (x + barWidth);
        let opacity = 1;
        if (distFromRight < fadeZone) {
          opacity = Math.max(0, distFromRight / fadeZone);
          opacity = opacity * opacity;
        }

        if (opacity < 0.03) continue;

        if (amplified > 0.15 && opacity > 0.35) {
          ctx.shadowColor = isDark
            ? `rgba(255, 255, 255, ${0.28 * opacity})`
            : `rgba(0, 0, 0, ${0.16 * opacity})`;
          ctx.shadowBlur = 5;
        } else {
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
        }

        ctx.fillStyle = `rgba(${barRgb}, ${opacity})`;
        const radius = Math.min(barWidth / 2, halfHeight);
        roundRect(
          ctx,
          x,
          centerY - halfHeight,
          barWidth,
          halfHeight * 2,
          radius
        );
        ctx.fill();
      }

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
    };
  }, [barGap, barWidth, peaksRef]);

  return (
    <div
      className={cn(
        "relative w-full h-full",
        // Sağ kenarda kısa/keskin fade (sola fazla yayılmasın)
        "[mask-image:linear-gradient(to_right,black_0%,black_88%,transparent_100%)]",
        "[-webkit-mask-image:linear-gradient(to_right,black_0%,black_88%,transparent_100%)]",
        className
      )}
    >
      <canvas ref={canvasRef} className="block w-full h-full" aria-hidden />
    </div>
  );
};

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

export default LiveWaveform;
